import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export function useCustomerConfig(cid) {
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const timers = useRef({});
  const inflight = useRef(0);
  const pendingVals = useRef({});

  useEffect(() => {
    const flush = () => {
      Object.entries(pendingVals.current).forEach(([field, value]) => {
        clearTimeout(timers.current[field]);
        api.patch(`/customers/${cid}`, { [field]: value }).catch(() => {});
      });
      pendingVals.current = {};
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, [cid]);

  useEffect(() => {
    let alive = true;
    setC(null);
    api
      .get(`/customers/${cid}`)
      .then((r) => alive && setC(r.data))
      .catch((e) => alive && setError(errMsg(e)));
    return () => {
      alive = false;
    };
  }, [cid]);

  const save = useCallback(
    (field, value, delay = 450) => {
      setC((p) => ({ ...p, [field]: value }));
      setSaveState("saving");
      clearTimeout(timers.current[field]);
      pendingVals.current[field] = value;
      timers.current[field] = setTimeout(async () => {
        delete pendingVals.current[field];
        inflight.current += 1;
        try {
          await api.patch(`/customers/${cid}`, { [field]: value });
          inflight.current -= 1;
          if (!inflight.current) {
            setSaveState("saved");
            toast.success("Endringer lagret automatisk", { id: "autosave", duration: 1500 });
          }
        } catch (e) {
          inflight.current -= 1;
          setSaveState("error");
          toast.error(errMsg(e, "Kunne ikke lagre endringen"));
        }
      }, delay);
    },
    [cid]
  );

  const setLocal = useCallback((field, value) => setC((p) => ({ ...p, [field]: value })), []);

  return { c, error, save, saveState, setLocal };
}
